import React, { Suspense, lazy } from 'react';

// Dynamic import wrapper for code splitting
export const createLazyComponent = <T extends React.ComponentType<any>>(
  importFunction: () => Promise<{ default: T }>,
  fallback?: React.ReactNode
) => {
  const LazyComponent = lazy(importFunction);
  
  return React.forwardRef<any, React.ComponentProps<T>>((props, ref) => (
    <Suspense fallback={fallback || <div className="animate-pulse bg-gray-200 rounded h-32"></div>}>
      <LazyComponent {...props} ref={ref} />
    </Suspense>
  ));
};

// Preload function for better UX
export const preloadComponent = (importFunction: () => Promise<any>) => {
  const componentImport = importFunction();
  return componentImport;
};

// Bundle size optimization utilities
export const BlogElementsLazy = {
  CalloutBox: createLazyComponent(
    () => import('../BlogElements/CalloutBox'),
    <div className="animate-pulse bg-blue-500/10 rounded-xl h-24 border border-blue-500/20"></div>
  ),
  StatisticsCard: createLazyComponent(
    () => import('../BlogElements/StatisticsCard'),
    <div className="animate-pulse bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl h-32"></div>
  ),
  TestimonialCard: createLazyComponent(
    () => import('../BlogElements/TestimonialCard'),
    <div className="animate-pulse bg-white/5 rounded-xl h-40 border border-white/10"></div>
  ),
  ComparisonTable: createLazyComponent(
    () => import('../BlogElements/ComparisonTable'),
    <div className="animate-pulse bg-white/5 rounded-xl h-64 border border-white/10"></div>
  ),
};

// Preload critical components on hover or intersection
export const usePreloadOnHover = (componentImport: () => Promise<any>) => {
  const preload = React.useCallback(() => {
    preloadComponent(componentImport);
  }, [componentImport]);

  return {
    onMouseEnter: preload,
    onFocus: preload,
  };
};

export default createLazyComponent;